import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Card,
    Row,
    Col,
    Typography,
    Divider,
    Image,
    Space,
    Button,
    Table,
    message,
    Tag
} from 'antd';
import {
    FileTextOutlined,
    ToolOutlined,
    SafetyCertificateOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import ImageGallery from '../../components/ImageGallery';
import InformationSection from '../../components/InformationSection';
import { generateInspectionPdf } from '../../services/GetPdf';
import { updateRequestData } from '../../services/updateRequestData';

// Reusable Component for Downloadable Attachments
const AttachmentLink = ({
    href,
    label,
    icon,
    color
}) => (
    <Col xs={24} md={8} style={{ padding: '16px', marginBottom: '16px' }}>
        <Typography.Text style={{ color, fontSize: '18px', display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            {React.cloneElement(icon, { style: { marginRight: 8, fontSize: '20px' } })}
            {label}:
        </Typography.Text>
        <Typography.Paragraph>
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className="downloadLink"
                style={{
                    textDecoration: 'none',
                    color: color || '#1890ff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    transition: 'background-color 0.3s ease',
                }}
                onMouseEnter={(e) => (e.target.style.backgroundColor = '#f0f0f0')}
                onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
            >
                <span>🔗</span>
                View {label}
            </a>
        </Typography.Paragraph>
    </Col>
);

const FlooringManufacturerRequest = ({
    requestData,
    setRequestData,
    attachments
}) => {
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state?.User?.userDetails);

    // Generic update handler for any field
    const handleUpdateField = (fieldName, newValue) => {
        const queryParams = new URLSearchParams(window.location.search);
        const req_id = queryParams.get("req_id");

        if (!req_id) {
            message.error('Request ID is missing');
            return;
        }

        // Create payload with the field to update
        const payload = {
            requestId: req_id,
            [fieldName]: newValue
        };

        // Dispatch the update action
        dispatch(updateRequestData(payload))
            .then((response) => {
                message.success(`${getFieldDisplayName(fieldName)} updated successfully`);
                // Update local state
                setRequestData({ ...requestData, [fieldName]: newValue });
            })
            .catch((error) => {
                message.error(`Failed to update ${getFieldDisplayName(fieldName).toLowerCase()}`);
                console.error(error);
            });
    };

    // Helper function to get display name for fields
    const getFieldDisplayName = (fieldName) => {
        const fieldDisplayNames = {
            concerns: 'Concerns',
            comments: 'Comments',
            manufacturer_name: 'Manufacturer Name',
            manufacturer_contact: 'Manufacturer Contact',
            manufacturer_phone_number: 'Manufacturer Phone Number',
            manufacturer_email: 'Manufacturer Email',
            hospitality_job_number: 'Hospitality Job Number',
            analyst_extension: 'Analyst Extension',
            account_executive: 'Account Executive',
            cell: 'Cell',
            manufacturer_address: 'Manufacturer Address',
            dealer_name: 'Dealer Name',
            dealer_contact: 'Dealer Contact',
            dealer_phone: 'Dealer Phone',
            dealer_email: 'Dealer Email',
            eu_name: 'Contact Person',
            eu_address: 'Address',
            eu_zip: 'Zip Code',
            eu_mob: 'Mobile Number',
            eu_phone2: 'Second Phone Number',
            eu_email: 'Email Address',
            job_name: 'Jobsite Name',
            style_name: 'Style Name/Number',
            color: 'Color',
            quanty: 'Quantity',
            sourced_product: 'Sourced Product',
            rvp: 'RVP',
            category: 'Category',
            job_stop: 'Job Stop',
            turn_key_labor: 'Turn Key Labor',
            concern_type: 'Property Type',
            inspection_type: 'Inspection Type',
            installed_by: 'Installed By',
            installation_date: 'Installation Date',
            problem_date: 'Date Problem Noticed'
        };
        return fieldDisplayNames[fieldName] || fieldName;
    };

    // Method to trigger PDF download
    const downloadPdf = () => {
        setLoading(true);
        const queryParams = new URLSearchParams(window.location.search);
        const req_id = queryParams.get("req_id");

        if (!req_id) {
            console.error("Request ID is missing");
            setLoading(false);
            return;
        }

        dispatch(generateInspectionPdf(req_id))
            .then((res) => {
                console.info("PDF received:", res);
                window.open(res);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching PDF:", err);
                setLoading(false);
            });
    };

    // Validate incoming data
    if (!requestData || requestData.request_type !== 'Flooring Manufacturer') {
        return <Typography.Text type="warning">Invalid Report Data</Typography.Text>;
    }

    const isExternalInspector = userDetails?.inspector_type === 'external';
    const isInspector = userDetails?.user_role === 'um_inspector';

    // Prepare product details columns
    const productDetailsColumns = [
        { title: "Style Name", dataIndex: "styleName", key: "styleName" },
        { title: "Color", dataIndex: "color", key: "color" },
        { title: "Back", dataIndex: "back", key: "back" },
        { title: "Lot", dataIndex: "lot", key: "lot" },
        { title: "Sysf", dataIndex: "sysf", key: "sysf" },
        ...(!isExternalInspector
            ? [{ title: "Unit Price", dataIndex: "unitPrice", key: "unitPrice" }]
            : [])
    ];

    // Prepare product details data source
    const productDetailsDataSource = (requestData.product_details || []).map((product, index) => {
        const baseDetails = {
            key: index,
            styleName: product["style-name"] || "-",
            color: product["product-color"] || "-",
            back: product["product-back"] || "-",
            lot: product["product-lot"] || "-",
            sysf: product["product-sysf"] || "-",
        };

        if (!isExternalInspector) {
            baseDetails.unitPrice = product["product-unitprice"] || "-";
        }

        return baseDetails;
    });

    const rawType = requestData?.inspection_request_type;
    const typeStr = Array.isArray(rawType)
        ? (rawType[0] || "")
        : (typeof rawType === 'string' ? rawType : (rawType ? String(rawType) : ""));
    const reqTypeNormalized = typeStr.trim().toLowerCase();

    return (
        <Card
            className="reportCard"
            hoverable
            style={{
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                borderRadius: '8px',
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Pinned Ribbon to the Right Side */}
            {typeStr && (
                <div 
                    className="inspection-type-ribbon"
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '0',
                        background: reqTypeNormalized === "fs resolution request" ? '#1890ff' : '#e67e22',
                        color: '#fff',
                        padding: '6px 16px 6px 20px',
                        borderTopLeftRadius: '20px',
                        borderBottomLeftRadius: '20px',
                        fontWeight: '600',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: '-2px 2px 5px rgba(0,0,0,0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 10
                    }}
                >
                    <span style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        background: '#fff',
                        marginRight: '8px',
                        display: 'inline-block'
                    }}></span>
                    {typeStr}
                </div>
            )}

            {/* Header with Logo and Claim Details */}
            <div className="reportCardHeader">
                <Row gutter={30} align="middle">
                    {requestData?.manufacturer_logo && (
                        <Col xs={24} md={4}>
                            <Image
                                src={requestData.manufacturer_logo}
                                className="img-fluid manuFLogo"
                                alt="Manufacturer Logo"
                                preview={false}
                                style={{
                                    maxHeight: '100px',
                                    objectFit: 'contain'
                                }}
                            />
                        </Col>
                    )}
                    <Col xs={24} md={20}>
                        <Space direction="vertical" size="small">
                            <Typography.Title level={4} className="inspcTitle">
                                Claim No.: {requestData?.claim_number || 'N/A'}
                            </Typography.Title>
                            {requestData?.purchase_order && (
                                <Typography.Text>
                                    POA: {requestData.purchase_order}
                                </Typography.Text>
                            )}
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Manufacturer Details - All fields editable */}
            <InformationSection
                title="Manufacturer"
                fields={[
                    { 
                        label: "Manufacturer Name", 
                        value: requestData?.manufacturer_name,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('manufacturer_name', newValue)
                    },
                    { 
                        label: "Contact", 
                        value: requestData?.manufacturer_contact,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('manufacturer_contact', newValue)
                    },
                    { 
                        label: "Phone Number", 
                        value: requestData?.manufacturer_phone_number,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('manufacturer_phone_number', newValue)
                    },
                    { 
                        label: "Email Address", 
                        value: requestData?.manufacturer_email,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('manufacturer_email', newValue)
                    },
                    { 
                        label: "Hospitality Job Number", 
                        value: requestData?.hospitality_job_number,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('hospitality_job_number', newValue)
                    },
                    { 
                        label: "Analyst Extension", 
                        value: requestData?.analyst_extension,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('analyst_extension', newValue)
                    },
                    { 
                        label: "Account Executive", 
                        value: requestData?.account_executive,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('account_executive', newValue)
                    },
                    { 
                        label: "Cell", 
                        value: requestData?.cell,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('cell', newValue)
                    },
                ]}
            />

            {/* Manufacturer's Address - Editable */}
            <InformationSection
                title="Manufacturer's Address to Ship Samples"
                fields={[
                    { 
                        label: "Address", 
                        value: requestData?.manufacturer_address,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('manufacturer_address', newValue)
                    }
                ]}
                fullWidth
            />

            {/* Dealer Details - All fields editable */}
            <InformationSection
                title="Dealer"
                fields={[
                    { 
                        label: "Dealer Name", 
                        value: requestData?.dealer_name,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('dealer_name', newValue)
                    },
                    { 
                        label: "Contact", 
                        value: requestData?.dealer_contact,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('dealer_contact', newValue)
                    },
                    { 
                        label: "Phone Number", 
                        value: requestData?.dealer_phone,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('dealer_phone', newValue)
                    },
                    { 
                        label: "Email Address", 
                        value: requestData?.dealer_email,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('dealer_email', newValue)
                    },
                ]}
            />

            {/* Inspection Site - All fields editable */}
            <InformationSection
                title="Inspection Site"
                fields={[
                    { 
                        label: "Contact Person", 
                        value: requestData?.eu_name,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_name', newValue)
                    },
                    { 
                        label: "Address", 
                        value: requestData?.eu_address,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_address', newValue)
                    },
                    { 
                        label: "Zip Code", 
                        value: requestData?.eu_zip || "-",
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_zip', newValue)
                    },
                    { 
                        label: "Mobile Number", 
                        value: requestData?.eu_mob,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_mob', newValue)
                    },
                    { 
                        label: "Second Phone Number", 
                        value: requestData?.eu_phone2,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_phone2', newValue)
                    },
                    { 
                        label: "Email Address", 
                        value: requestData?.eu_email,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('eu_email', newValue)
                    },
                    { 
                        label: "Jobsite Name", 
                        value: requestData?.job_name,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('job_name', newValue)
                    },
                ]}
            />

            {/* Product to Inspect - Editable fields */}
            {(requestData?.style_name || requestData?.color || requestData?.quanty) && (
                <InformationSection
                    title="Product to Inspect"
                    fields={[
                        { 
                            label: "Style Name/Number", 
                            value: requestData?.style_name,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('style_name', newValue)
                        },
                        { 
                            label: "Color", 
                            value: requestData?.color,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('color', newValue)
                        },
                        { 
                            label: "Quantity", 
                            value: requestData?.quanty,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('quanty', newValue)
                        },
                        
                    ]}
                />
            )}

            {/* Product Details Table */}
            {requestData?.product_details?.length > 0 && (
                <>
                    <Divider orientation="left">
                        <Typography.Text strong style={{ fontSize: '18px' }}>
                            Product Details
                        </Typography.Text>
                    </Divider>
                    <Table
                        dataSource={productDetailsDataSource}
                        columns={productDetailsColumns}
                        pagination={false}
                    />
                </>
            )}

            {/* Invoice Details - Conditionally Rendered */}
            {requestData?.invoice_details?.length > 0 && !isExternalInspector && (
                <>
                    <Divider orientation="left">
                        <Typography.Text strong style={{ fontSize: '18px' }}>
                            Invoice Details
                        </Typography.Text>
                    </Divider>
                    <Table
                        dataSource={requestData.invoice_details.map((invoice, index) => ({
                            key: index,
                            invoice: invoice["invoice"] || "-",
                            date: invoice["invoice-date"] || "-",
                            amount: invoice["invoice-amount"] || "-",
                            installDate: invoice["invoice-install-date"] || "-",
                        }))}
                        columns={[
                            { title: "Invoice Number", dataIndex: "invoice", key: "invoice" },
                            { title: "Date", dataIndex: "date", key: "date" },
                            { title: "Amount", dataIndex: "amount", key: "amount" },
                            { title: "Install Date", dataIndex: "installDate", key: "installDate" },
                        ]}
                        pagination={false}
                    />
                </>
            )}

            {/* Flooring Category - Editable */}
            <InformationSection
                title="Flooring Category"
                fields={[
                    { 
                        label: "Category", 
                        value: requestData?.category,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('category', newValue)
                    },
                ]}
            />

            {/* Concerns - Already editable */}
            <InformationSection
                title="Concerns"
                fields={[
                    { 
                        label: "Concerns", 
                        value: requestData?.concerns,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('concerns', newValue)
                    },
                ]}
                fullWidth
            />

            {/* Inspection Details - All fields editable */}
            <InformationSection
                title="Inspection Details"
                fields={[
                    { 
                        label: "Job Stop", 
                        value: requestData?.job_stop,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('job_stop', newValue)
                    },
                    { 
                        label: "Turn Key Labor", 
                        value: requestData?.turn_key_labor,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('turn_key_labor', newValue)
                    },
                    { 
                        label: "Property Type", 
                        value: requestData?.concern_type,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('concern_type', newValue)
                    },
                    ...(requestData?.concern_type === 'Commercial' ? [
                        { 
                            label: "Sourced Product", 
                            value: requestData?.sourced_product,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('sourced_product', newValue)
                        },
                        { 
                            label: "RVP", 
                            value: requestData?.rvp,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('rvp', newValue)
                        }
                    ] : []),
                    { 
                        label: "Inspection Type", 
                        value: requestData?.inspection_type,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('inspection_type', newValue)
                    },
                ]}
            />

            {/* Installation Details - All fields editable */}
            <InformationSection
                title="Installation Details"
                fields={[
                    { 
                        label: "Installed By", 
                        value: requestData?.installed_by,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('installed_by', newValue)
                    },
                    { 
                        label: "Installation Date", 
                        value: requestData?.installation_date,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('installation_date', newValue)
                    },
                    { 
                        label: "Date Problem Noticed", 
                        value: requestData?.problem_date,
                        editable: !isInspector,
                        onSave: (newValue) => handleUpdateField('problem_date', newValue)
                    },
                ]}
            />

            {/* Comments Section - Already editable */}
            <Card
                className="innerCard mb-3"
                style={{
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px'
                }}
            >
                <InformationSection
                    title="Comments"
                    fields={[
                        { 
                            label: "Comments", 
                            value: requestData?.comments,
                            editable: !isInspector,
                            onSave: (newValue) => handleUpdateField('comments', newValue)
                        },
                    ]}
                    fullWidth
                />
            </Card>

            {/* Uploaded Photos */}
            {attachments && Object.keys(attachments).length > 0 && (
                <>
                    <Divider orientation="left">
                        <Typography.Text strong style={{ fontSize: '18px' }}>
                            Uploaded Photos
                        </Typography.Text>
                    </Divider>
                    <div>
                        <ImageGallery attachments={attachments} />
                    </div>
                </>
            )}

            {/* Additional Attachments and Instructions */}
            {(requestData?.additional_attachments?.length > 0 ||
                requestData?.installation_instructions ||
                requestData?.maintenence_instructions ||
                requestData?.warranty_information ||
                requestData?.hard_surface_checklist) && (
                    <>
                        <Divider orientation="left">
                            <Typography.Text strong style={{ fontSize: '18px' }}>
                                Additional Attachments and Instructions
                            </Typography.Text>
                        </Divider>
                        <Row gutter={16}>
                            {requestData?.additional_attachments?.length > 0 && requestData?.additional_attachments?.map((attach, index) => (
                                <AttachmentLink
                                    key={index}
                                    href={attach}
                                    label={`Additional Attachment ${index + 1}`}
                                    icon={<FileTextOutlined />}
                                    color="#007BFF"
                                />
                            ))}

                            {requestData?.installation_instructions && (
                                <AttachmentLink
                                    href={requestData.installation_instructions}
                                    label="Installation Instructions"
                                    icon={<FileTextOutlined />}
                                    color="#007BFF"
                                />
                            )}

                            {requestData?.maintenence_instructions && (
                                <AttachmentLink
                                    href={requestData.maintenence_instructions}
                                    label="Care and Maintenance Instructions"
                                    icon={<ToolOutlined />}
                                    color="#28A745"
                                />
                            )}

                            {requestData?.warranty_information && (
                                <AttachmentLink
                                    href={requestData.warranty_information}
                                    label="Warranty Information"
                                    icon={<SafetyCertificateOutlined />}
                                    color="#FFC107"
                                />
                            )}

                            {requestData?.hard_surface_checklist && (
                                <AttachmentLink
                                    href={requestData.hard_surface_checklist}
                                    label="Hard Surface Checklist"
                                    icon={<CheckCircleOutlined />}
                                    color="#DC3545"
                                />
                            )}
                        </Row>
                    </>
                )}

            <div className='d-flex justify-content-center mt-4 mb-4'>
                <Button
                    className="btnPrimary"
                    onClick={downloadPdf}
                    disabled={loading}
                >
                    {loading ? "Downloading..." : "Download PDF"}
                </Button>
            </div>
        </Card>
    );
};

export default FlooringManufacturerRequest;